import { toast as sonnerToast, type ExternalToast } from 'sonner'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastOptions {
  duration?: number
  action?: ToastAction
  cancel?: ToastAction
  onDismiss?: () => void
  onAutoClose?: () => void
}

type ToastVariant = 'success' | 'error' | 'info' | 'warning' | 'loading' | 'default'

const buildOptions = (options?: ToastOptions): ExternalToast | undefined => {
  if (!options) {
    return undefined
  }

  const cleaned: Record<string, unknown> = {}

  if (options.duration !== undefined) {
    cleaned['duration'] = options.duration
  }
  if (options.action) {
    cleaned['action'] = options.action
  }
  if (options.cancel) {
    cleaned['cancel'] = options.cancel
  }
  if (options.onDismiss) {
    cleaned['onDismiss'] = options.onDismiss
  }
  if (options.onAutoClose) {
    cleaned['onAutoClose'] = options.onAutoClose
  }

  return cleaned as ExternalToast
}

const showToast = (variant: ToastVariant, message: string, options?: ToastOptions) => {
  const toastOptions = buildOptions(options)

  switch (variant) {
    case 'success':
      return sonnerToast.success(message, toastOptions)
    case 'error':
      return sonnerToast.error(message, toastOptions)
    case 'info':
      return sonnerToast.info(message, toastOptions)
    case 'warning':
      return sonnerToast.warning(message, toastOptions)
    case 'loading':
      return sonnerToast.loading(message, toastOptions)
    default:
      return sonnerToast(message, toastOptions)
  }
}

export const toast = {
  success: (msg: string, opts?: ToastOptions) => showToast('success', msg, opts),
  error: (msg: string, opts?: ToastOptions) => showToast('error', msg, opts),
  info: (msg: string, opts?: ToastOptions) => showToast('info', msg, opts),
  warning: (msg: string, opts?: ToastOptions) => showToast('warning', msg, opts),
  loading: (msg: string, opts?: ToastOptions) => showToast('loading', msg, opts),
  default: (msg: string, opts?: ToastOptions) => showToast('default', msg, opts),

  promise: <T>(
    promise: Promise<T>,
    {
      loading,
      success,
      error,
      ...options
    }: {
      loading: string
      success: string | ((data: T) => string)
      error: string | ((err: unknown) => string)
    } & ToastOptions,
  ) => {
    const toastOptions = { ...buildOptions(options), loading, success, error }
    return sonnerToast.promise(promise, toastOptions)
  },

  dismiss: (id?: string | number) => sonnerToast.dismiss(id),
}
